import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import styles from '../../search.module.scss'
import { FilterDropdown } from '@/components/search/FilterDropdown'
import { Checkbox } from '@/components/search/inputs/Checkbox'
import { FILTER_DELIVERY, FILTER_INCLUSIVE_BENEFITS } from '@/lib/constants'
import { filterLabelPrettyName, filterClassName } from '@/lib/utilities/search'
import {
  selectActiveDeliveryTimeVehiclePriceTags,
  selectActiveInclusiveBenefitsVehiclePriceTags,
  selectAvailableLeadTimes,
  selectAvailableVehiclePriceTagsWithoutLeadTimes,
} from '@/store/selectors/search'
import {
  SelectComponentProps,
  VehiclePriceTag,
  VehiclePriceTagSearchRequest,
  VehiclePriceTagSubTag,
} from '@/types/search'
import { isProd } from '@/lib/utilities/system'

export const AvailableVehiclePriceTagsMultiSelect = ({
  toggleFilter,
  onChange,
  isFilterOpen,
  title,
  filter,
}: SelectComponentProps) => {
  const activeDeliveryTimeTags = useSelector(selectActiveDeliveryTimeVehiclePriceTags)
  const activeInclusiveBenefits = useSelector(selectActiveInclusiveBenefitsVehiclePriceTags)
  const availableDeliveryTimeTags = useSelector(selectAvailableLeadTimes)
  const availableInclusiveBenefitsTags = useSelector(
    selectAvailableVehiclePriceTagsWithoutLeadTimes,
  )

  const [selectedOptions, setSelectedOptions] = useState<VehiclePriceTagSearchRequest[]>([])

  useEffect(() => {
    setSelectedOptions(
      filter === FILTER_DELIVERY ? activeDeliveryTimeTags : activeInclusiveBenefits,
    )
  }, [activeInclusiveBenefits, activeDeliveryTimeTags, filter])

  return (
    <FilterDropdown
      title={title}
      toggleFilter={toggleFilter}
      isFilterOpen={isFilterOpen}
      maximumNumberOfVisibleSelectedOptions={3}
      hasSelectedOptions={selectedOptions?.length > 0}
      selectedOptions={
        selectedOptions?.length > 0
          ? filter === FILTER_DELIVERY
            ? [...selectedOptions[0].vehiclePriceTagIds]
              .sort((a, b) => a - b)
              .map((selected: number) =>
                filterLabelPrettyName(
                  FILTER_DELIVERY,
                  availableDeliveryTimeTags.filter(
                    (available: VehiclePriceTag) => available.id === selected,
                  )?.[0]?.filterLabel || null,
                  undefined,
                  true,
                ),
              )
            : selectedOptions.map(
              (selected: VehiclePriceTagSearchRequest) =>
                availableInclusiveBenefitsTags.filter(
                  (available: VehiclePriceTag) =>
                    available.id === selected.vehiclePriceTagCategoryId,
                )[0]?.name,
            )
          : ['All']
      }>
      <div className={styles['multi-select']}>
        {filter === FILTER_INCLUSIVE_BENEFITS && (
          <>
            {availableInclusiveBenefitsTags.length > 0 ?
              availableInclusiveBenefitsTags.map((option: VehiclePriceTag) => (
                <Checkbox
                  item={option}
                  isSelected={
                    selectedOptions.findIndex(
                      (active: VehiclePriceTagSearchRequest) =>
                        active.vehiclePriceTagCategoryId === option.id,
                    ) > -1
                  }
                  valueAttribute="id"
                  onChange={params =>
                    onChange({
                      ...params,
                      filter,
                      isGrouped: true,
                      vehiclePriceTag: {
                        category: option.id,
                      },
                    })
                  }
                  filter={filter}
                  key={option.id}
                  type="vehiclePriceTag"
                  className={!isProd() ? `search__${filter}-button search__${filter}-button-${filterClassName(filterLabelPrettyName(filter, option.name))}` : ''}
                />
              ))
              : (
                <p className={styles['error-text']}>
                  {`There are no "${title}" options available based on your current filter selections`}
                </p>
              )}
          </>)}

        {filter === FILTER_DELIVERY && (
          <>
            {availableDeliveryTimeTags.length > 0 ?
              availableDeliveryTimeTags.map((option: VehiclePriceTagSubTag) => (
                <Checkbox
                  item={{
                    id: option.id,
                    name: option.filterLabel,
                  }}
                  isSelected={activeDeliveryTimeTags[0]?.vehiclePriceTagIds.includes(option.id)}
                  valueAttribute="id"
                  onChange={params =>
                    onChange({
                      ...params,
                      filter,
                      isGrouped: false,
                      vehiclePriceTag: {
                        category: 3,
                        tag: option.id,
                      },
                    })
                  }
                  resultCount={option.filterStatus === 'UNAVAILABLE' ? 0 : undefined}
                  showResultCount={false}
                  filter={filter}
                  key={option.id}
                  type="vehiclePriceTag"
                  className={!isProd() ? `search__${filter}-button search__${filter}-button-${filterClassName(option.filterLabel)}` : ''}
                />
              ))
              : (
                <p className={styles['error-text']}>
                  {`There are no "${title}" options available based on your current filter selections`}
                </p>
              )}
          </>)}
      </div>
    </FilterDropdown>
  )
}
